/**
 * The sentence a failed API call shows the user.
 *
 * The backend refuses a request with `{error:{code,message}}`, and that message
 * is usually the one thing the user needs — "ca này đã đủ 2 người tại cơ sở",
 * "Báo giá đang chờ duyệt — chưa thu tiền được". Hooks that threw a fixed
 * "Failed" / "Lỗi" instead hid the reason on 57 write buttons (audit
 * 23/09/2026). Every write hook builds its error with `serverErrorMessage`.
 *
 * Rules:
 *   - a Vietnamese message is shown as it is — except "<thing> not found",
 *     which errs.NotFound builds with an English tail;
 *   - an English message is never shown raw: a known one is translated, an
 *     unknown one falls back to a sentence for its error code, then to the
 *     caller's own fallback;
 *   - no message → the caller's fallback;
 *   - an internal error, or a body that is not JSON at all → the caller's
 *     fallback plus what kind of failure it was (server error / server down).
 */

/** Shown instead of the browser's "Failed to fetch" when the server is unreachable. */
export const NETWORK_ERROR_MESSAGE = "Mất kết nối tới máy chủ — kiểm tra mạng rồi thử lại";

const VIETNAMESE_LETTER =
  /[ăâđêôơưàáảãạằắẳẵặầấẩẫậèéẻẽẹềếểễệìíỉĩịòóỏõọồốổỗộờớởỡợùúủũụừứửữựỳýỷỹỵ]/iu;

type Translation = [RegExp, string | ((m: RegExpExecArray) => string)];

// English messages the backend still sends on user-facing paths, in the words
// the user needs. Developer-only messages (a missing id, a malformed JSON
// field) are left out on purpose: the form already prevents them, and the
// caller's fallback reads better than a translated field name.
const ENGLISH: Translation[] = [
  // Someone else changed the record first.
  [/^resource version does not match$/, "Người khác vừa sửa mục này — tải lại rồi thử lại"],
  [/^assetIds must not exceed 500$/, "Mỗi lần tối đa 500 ảnh — bỏ bớt ảnh đã chọn rồi làm từng đợt"],
  [/^assetIds is required$/, "Chưa chọn ảnh nào"],

  // Customer self-booking (CSKH).
  [/^an active overlapping booking already exists$/, "Khách đã có một lịch khác trùng giờ — kiểm lịch cũ trước"],
  [/^slot is unavailable$/, "Khung giờ này đã hết chỗ — chọn giờ khác"],
  [/^booking cannot be confirmed from its current status$/, "Lịch này không còn chờ xác nhận — tải lại để xem trạng thái mới"],
  [/^booking transition is not allowed$/, "Lịch này đã đổi trạng thái — tải lại để xem"],
  [/^only confirmed bookings can be converted$/, "Chỉ nối được lịch đã xác nhận"],
  [/^order schedule does not match booking$/, "Giờ chụp của đơn không khớp lịch khách đặt — sửa lịch đơn cho khớp trước"],
  // Tạo đơn từ lượt khách tự đặt (QĐ 0278) — các câu của customerbooking.validateOrderConversion.
  [/^only confirmed or paid public bookings can be converted$/, "Lịch khách này không còn ở trạng thái đã xác nhận — tải lại Booking online để xem"],
  [/^order schedule must exactly match booking$/, "Đơn phải giữ đúng ngày và giờ khách đã đặt — đổi lịch ở Booking online trước"],
  [/^anonymized booking cannot be converted$/, "Lịch khách này đã ẩn thông tin cá nhân — không tạo đơn từ đây được"],
  [/^linked booking branch is not enabled for booking capacity$/, "Chi nhánh của lịch này chưa nhận đặt lịch online — tạo đơn thường ở màn Đơn hàng"],
  [/^cancelled order cannot convert a customer booking$/, "Đơn huỷ không nối được với lịch khách"],
  [/^cancelled order cannot be linked$/, "Đơn đã huỷ — không nối được với lịch khách"],
  [/^order is already linked to another booking$/, "Đơn này đã nối với một lịch khách khác"],
  [/^slot must be in the future$/, "Giờ chọn đã qua — chọn giờ sau thời điểm hiện tại"],
  [/^slot is outside the public window$/, "Giờ chọn nằm ngoài khung giờ nhận lịch"],
  [/^slot must satisfy minimum lead time$/, "Giờ chọn quá sát — cần đặt trước lâu hơn"],
  [/^slot must be within 60 calendar days$/, "Chỉ đặt được lịch trong vòng 60 ngày tới"],
  [/^customer booking not found$/, "Không tìm thấy lịch khách — tải lại trang"],

  // Online booking page and "Lịch của tôi" — the customer reads these.
  [/^branch is not open for customer booking$/, "Cơ sở này chưa nhận đặt lịch online — chọn cơ sở khác hoặc nhắn CSKH"],
  [/^email is invalid$/, "E-mail chưa đúng — kiểm tra lại"],
  [/^email is too long$/, "E-mail quá dài — kiểm tra lại"],
  [/^only \+84 phone country code is supported$/, "Chỉ nhận số điện thoại Việt Nam (+84)"],
  [/^only confirmed bookings can request reschedule$/, "Chỉ đổi được lịch đã được studio xác nhận"],
  [/^booking is not awaiting payment$/, "Lịch này không còn chờ thanh toán — tải lại trang để xem trạng thái mới"],
  [/^from must not be in the past$/, "Ngày chọn đã qua — chọn ngày khác"],
  [/^to exceeds booking horizon$/, "Ngày chọn quá xa — chỉ đặt được trong khoảng ngày studio đang nhận"],
  [/^turnstile (?:verification failed|token is required)$/, "Chưa xác minh được lượt gửi — chờ ô kiểm tra hiện dấu tích rồi gửi lại"],
  // The choice was withdrawn from the catalogue while the page stood open.
  [/^(?:service|concept|purpose|payment option) is invalid$/, "Lựa chọn dịch vụ vừa thay đổi — tải lại trang rồi chọn lại"],

  // Payments & bank transfers.
  [/^payment is not pending\b/, "Phiếu này đã được xử lý (xác nhận hoặc từ chối) — tải lại để xem trạng thái mới"],
  [/^payment is already linked to a bank transaction$/, "Phiếu này đã khớp với một giao dịch ngân hàng — tiền đã về"],
  [/^QR is only available for bank transfer payments$/, "Chỉ phiếu chuyển khoản mới tạo được mã QR"],
  [/^QR is not expired$/, "Mã QR chưa hết hạn"],
  [/^QR is not a counter payment QR$/, "Đây không phải mã QR tại quầy"],
  [/^amount exceeds remaining balance$/, "Số tiền vượt quá số còn phải thu của đơn"],
  [/^amount must be positive$/, "Số tiền phải lớn hơn 0"],
  [/^amount must be ≥ 0$/, "Số tiền không được âm"],
  [/^payment already linked to a different bank transaction$/, "Phiếu thu này đã khớp với một giao dịch ngân hàng khác"],
  [
    /^bank transaction already linked(?: to (?:another|an unconfirmed) payment)?$/,
    "Giao dịch ngân hàng này đã gắn vào một phiếu thu khác",
  ],
  [/^payment for bank_txn_id already exists\b/, "Giao dịch ngân hàng này đã có phiếu thu"],

  // Shift registration (reception + ekip) and assigned shoots.
  [/^registration is locked for this week$/, "Tuần này đã khoá đăng ký ca"],
  [/^letan shift already registered$/, "Bạn đã đăng ký ca này rồi"],
  [/^can only delete pending shifts$/, "Chỉ xoá được ca đang chờ duyệt"],
  [/^only the shift owner or a manager can delete$/, "Chỉ người đăng ký ca hoặc quản lý mới xoá được"],
  [/^only managers can lock weeks$/, "Chỉ quản lý mới khoá được tuần"],
  [/^only managers can unlock weeks$/, "Chỉ quản lý mới mở khoá được tuần"],
  [/^pending (?:ekip|letan) shift not found$/, "Ca này không còn chờ duyệt — có thể người khác vừa xử lý. Tải lại trang"],
  [/^approved (?:ekip|letan) shift not found$/, "Ca này không còn ở trạng thái đã duyệt — tải lại trang"],
  [/^(?:ekip |letan )?shift not found$/, "Không tìm thấy ca — có thể đã bị xoá. Tải lại trang"],
  [/^lock not found$/, "Tuần này chưa khoá"],
  [/^shift not assigned to actor$/, "Ca này không còn giao cho bạn — tải lại để xem người được phân công"],
  // One sentence for every refused order or shift transition (order/service.go).
  [/^role '.*' cannot transition from '.*' to '.*'$/, "Vai của bạn không được chuyển sang bước này"],
  [/^rawPhotoLinks must contain absolute http\(s\) URLs$/, "Link ảnh gốc phải là đường dẫn đầy đủ, bắt đầu bằng http:// hoặc https://"],
  [/^(?:destination )?branch is outside the (?:caller|authenticated branch) scope$/, "Cơ sở này nằm ngoài phạm vi bạn được giao"],

  // Schedule grid: the order already left the grid, usually because someone
  // else postponed it a moment earlier.
  [/^order has no schedule to postpone$/, "Đơn này không còn lịch chụp để hoãn — có thể người khác vừa hoãn. Tải lại trang"],

  // Retouch dispatch: the 15-minute offer closed, or someone took it first.
  [/^offer not found$/, "Lời chào này đã hết hạn hoặc đã có người nhận — tải lại trang"],

  // Hành trình mỗi ngày (QĐ 0468): máy chủ trả 404 cho cả "không có người này" lẫn
  // "không được xem cuốn ảnh người này" — cố ý không phân biệt để khỏi lộ.
  [/^journey not found$/, "Không tìm thấy hoặc bạn không được xem hành trình này"],

  // Order gallery: customer selection, uploads, albums, versions.
  [/^gallery selection is locked$/, "Khách đã chốt danh sách ảnh — mở lại lượt chọn rồi mới sửa được"],
  [/^gallery selection is already open$/, "Lượt chọn ảnh đang mở sẵn rồi"],
  [/^upload session has expired$/, "Lượt tải ảnh đã hết hạn — tải ảnh lên lại"],
  [/^upload session is not active$/, "Lượt tải ảnh này đã dừng — tải ảnh lên lại"],
  [/^clientUploadId belongs to a closed upload$/, "Lượt tải trước của các ảnh này đã hỏng giữa chừng — chọn lại ảnh để tải"],
  [/^uploadIds (?:is required|contains an invalid id|accepts at most \d+ ids)$/, "Không đọc được tiến độ xử lý ảnh — tải lại trang"],
  [/^file size exceeds maxFileBytes$/, "Ảnh vượt dung lượng cho phép — giảm cỡ ảnh rồi tải lại"],
  [/^contentType must be image\/jpeg, image\/png, or image\/webp$/, "Chỉ nhận ảnh JPEG, PNG hoặc WebP"],
  [/^upload batch exceeds maxBatchFiles$/, "Chọn quá nhiều ảnh cho một lần tải — chia làm nhiều lần"],
  [/^booking upload quota exceeded$/, "Đơn này đã dùng hết dung lượng ảnh được tải lên"],
  [/^only trashed assets can be restored$/, "Ảnh này không còn trong thùng rác — tải lại trang"],
  [/^root album cannot be (?:deleted|reparented)$/, "Không xoá hay di chuyển được album của đơn"],
  [/^album with child albums cannot be deleted$/, "Thư mục này còn thư mục con bên trong — chuyển hoặc xoá các thư mục con trước"],
  [/^album parent would create a cycle$/, "Không đặt được thư mục vào bên trong chính nó"],
  [/^gallery archive is not ready$/, "File nén đang tạo — chờ một lát rồi tải lại"],
  [/^asset version is not ready$/, "Ảnh đang được xử lý — chờ một lát rồi thử lại"],
  [/^only failed versions can be retried$/, "Chỉ chạy lại được ảnh bị lỗi xử lý"],
  [/^original object is no longer available$/, "Ảnh gốc không còn trên kho — tải ảnh lên lại"],
  [/^gallery share is already revoked$/, "Link này đã được thu hồi rồi"],
  // Ảnh máy sinh ra (Xưởng ghép, màu nước — QĐ 0233).
  [/^only the Final branch or the root of a flat album accept generated photos$/, "Chỉ thư mục Ảnh hoàn thiện (và thư mục con của nó), hoặc thư mục gốc của album phẳng, mới nhận ảnh ghép / ảnh màu nước"],
  [/^photo is not ready yet$/, "Ảnh chưa xử lý xong — đợi ảnh hiện bản xem rồi thử lại"],

  // Branch catalogue and Casdoor branch scopes.
  [/^branch code already exists$/, "Mã chi nhánh này đã có — chọn mã khác"],
  [/^code must match \[a-z0-9_\]\+$/, "Mã chi nhánh chỉ gồm chữ thường không dấu, số và dấu gạch dưới"],
  [/^code must be at most 20 characters$/, "Mã chi nhánh tối đa 20 ký tự"],
  [/^branch code is immutable$/, "Không đổi được mã chi nhánh sau khi đã tạo"],
  [/^branch nodes cannot have children$/, "Chi nhánh không chứa được đơn vị con — chỉ nhóm mới chứa được"],
  [/^only branch nodes can run shift operations$/, "Chỉ chi nhánh (không phải nhóm) mới bật được ca vận hành"],
  [/^only branch nodes can run a cash drawer$/, "Chỉ chi nhánh (không phải nhóm) mới bật được két tiền"],
  [/^only branch nodes can be publicly bookable$/, "Chỉ chi nhánh (không phải nhóm) mới mở cho khách tự đặt lịch"],
  [/^group nodes cannot have check-in coordinates$/, "Nhóm không có toạ độ chấm công — chỉ chi nhánh mới có"],
  [/^(?:branch hierarchy cannot contain a cycle|invalid branch hierarchy)$/, "Cây chi nhánh không hợp lệ — một đơn vị không thể nằm dưới chính nó"],
  [/^branch parent not found$/, "Không tìm thấy đơn vị cha — tải lại trang"],
  [/^branch not found$/, "Không tìm thấy chi nhánh — tải lại trang"],
  [/^capacity must be at least 1$/, "Sức chứa phải từ 1 trở lên"],
  [/^check-in radius must be positive$/, "Bán kính chấm công phải lớn hơn 0"],
  [/^latitude and longitude must be set together$/, "Cần nhập đủ cả vĩ độ và kinh độ"],
  [/^latitude must be between -90 and 90$/, "Vĩ độ phải trong khoảng -90 đến 90"],
  [/^longitude must be between -180 and 180$/, "Kinh độ phải trong khoảng -180 đến 180"],
  [/^letan max staff per shift must be at least 1$/, "Số lễ tân tối đa mỗi ca phải từ 1 trở lên"],
  [/^cash (?:float amount|remit threshold|variance threshold) cannot be negative$/, "Các mức tiền của két không được âm"],
  [/^branch saved but .*; run resync$/, "Đã lưu nhưng chưa đồng bộ Casdoor — bấm “Đồng bộ Casdoor” ở danh mục chi nhánh"],
  [/^staff is not linked to Casdoor$/, "Nhân viên này chưa có tài khoản đăng nhập Casdoor — chưa đọc được phạm vi chi nhánh"],
  [
    /^unknown Casdoor branch scope (.+)$/,
    (m) => `Phạm vi “${m[1]}” chưa có trên Casdoor — bấm “Đồng bộ Casdoor” ở danh mục chi nhánh rồi thử lại`,
  ],
  [/^Casdoor branch scope\b/, "Hệ đăng nhập Casdoor đang gián đoạn — chưa đọc được phạm vi, thử lại sau ít phút"],

  // Staff records and their login accounts.
  [/^staff id already exists$/, "Mã nhân viên này đã có"],
  [/^email is required when creating a Casdoor account$/, "Cần nhập e-mail để tạo tài khoản đăng nhập"],
  [/^role is required when creating a Casdoor account$/, "Cần chọn vai để tạo tài khoản đăng nhập"],
  [/^branch must be a specific branch, not a group$/, "Chọn một chi nhánh cụ thể, không chọn nhóm"],

  // Customers.
  [/^(?:invalid phone number|phone is invalid)$/, "Số điện thoại không hợp lệ"],
  [/^name is required$/, "Cần nhập tên"],

  // Admin data sync.
  [/^another data sync run is active$/, "Đang có một lượt đồng bộ khác chạy — chờ lượt đó xong rồi thử lại"],
  [/^data sync is disabled$/, "Tính năng đồng bộ dữ liệu đang tắt"],
  [/^confirm must equal RECONCILE$/, "Gõ đúng chữ RECONCILE để xác nhận"],

  // Business plan.
  [/^profitMargin(?:Target|Current) must be in \[0,100\]$/, "Biên lợi nhuận phải trong khoảng 0–100%"],
  [/^yearlyRevenue too large$/, "Doanh thu năm quá lớn — kiểm lại số"],
  [/^yearlyRevenue must be > 0$/, "Doanh thu năm phải lớn hơn 0"],

  // Image uploads: note images (complaints, bills, order notes) and staff
  // photos — note/upload.go, profile/upload.go.
  [
    /^could not decode image header\b/,
    "Ảnh không đọc được — có thể ảnh bị hỏng hoặc không phải ảnh JPG/PNG thật. Chọn ảnh khác",
  ],
  [/^unsupported contentType .* \(allowed: jpeg\/png\/webp\/gif;/, "Chỉ nhận ảnh JPG, PNG, WebP hoặc GIF — chọn ảnh khác"],
  [/^unsupported contentType .* \(allowed: jpeg\/png\/webp;/, "Chỉ nhận ảnh JPG, PNG hoặc WebP — chọn ảnh khác"],
  // A file over the size cap is cut off mid-body, so the form arrives without it.
  [
    /^missing form field 'file'$/,
    "Máy chủ không nhận được ảnh — ảnh có thể quá lớn hoặc mạng chập chờn. Chọn ảnh nhỏ hơn rồi thử lại",
  ],
  [/^staff ".*" not found$/, "Không tìm thấy nhân viên này — có thể đã bị xoá. Tải lại trang"],

  // Bảng việc Sửa hình (GET /retouch/queue). Màn chỉ gửi giá trị có sẵn; câu
  // này chỉ hiện khi đường dẫn bị gõ tay hoặc màn cũ gọi lệch phiên bản.
  [
    /^invalid '(?:tab|delivery|deadline_basis|deadline)' \(expect .*\)$/,
    "Bộ lọc không hợp lệ — tải lại trang rồi chọn lại",
  ],
  [/^invalid deadline date \(expect YYYY-MM-DD\)$/, "Ngày lọc hạn không hợp lệ — chọn lại ngày"],

  // Shared.
  [/^reason is required$/, "Cần ghi lý do"],
  [/^invalid date$/, "Ngày không hợp lệ"],
  // A reversed date range, in the four wordings the lists use (staffcheckin,
  // rankings, schedule/closures/letan/complaint/dashboard/customerbooking/
  // ekipshift, datasync). The check-in history used to show it as an empty table.
  [
    /^(?:from must be before to|'from' must be before 'to'|to must be on or after from|from must not be after to)$/,
    "Ngày “Từ” đang sau ngày “Đến” — chọn lại khoảng ngày",
  ],
];

// Last resort for an English message not in the table: what the error code
// itself tells the user. Codes whose meaning depends on the message
// (validation, conflict, internal) fall through to the caller's fallback.
const BY_CODE: Record<string, string> = {
  version_conflict: "Người khác vừa sửa mục này — tải lại rồi thử lại",
  invalid_transition: "Mục này đã đổi trạng thái — tải lại rồi thử lại",
  active_booking_exists: "Khách đã có một lịch khác trùng giờ — kiểm lịch cũ trước",
  slot_unavailable: "Khung giờ này đã hết chỗ — chọn giờ khác",
  forbidden: "Bạn không có quyền làm việc này",
  not_found: "Không tìm thấy dữ liệu — có thể đã bị xoá hoặc người khác vừa xử lý. Tải lại trang",
  unauthorized: "Phiên đăng nhập đã hết — đăng nhập lại",
  service_unavailable: "Hệ thống liên quan đang gián đoạn — thử lại sau ít phút",
  storage_disabled: "Kho lưu ảnh đang tắt — báo quản trị hệ thống",
  rate_limited: "Thao tác quá nhanh — chờ một lát rồi thử lại",
  request_in_progress: "Yêu cầu trước vẫn đang xử lý — chờ một lát rồi tải lại",
  branch_policy_sync_failed: "Đã lưu nhưng chưa đồng bộ Casdoor — bấm “Đồng bộ Casdoor” ở danh mục chi nhánh",
};

/**
 * errs.NotFound("<thing>") answers "<thing> not found" even when the thing is
 * Vietnamese ("đơn nghỉ not found"). A short name becomes a whole sentence; a
 * name that already says it ("không thấy đơn") loses the English tail. Any
 * other Vietnamese message stays as it is.
 */
function vietnameseNotFound(message: string): string | undefined {
  const thing = /^(.+) not found$/.exec(message)?.[1]?.trim();
  if (!thing) return undefined;
  if (/^không /iu.test(thing)) return thing;
  if (!/^[\p{L}\p{M}/ ]+$/u.test(thing) || thing.split(/\s+/).length > 6) return undefined;
  return `Không tìm thấy ${thing.charAt(0).toLowerCase()}${thing.slice(1)} — có thể đã bị xoá. Tải lại trang`;
}

function translateEnglish(message: string): string | undefined {
  for (const [pattern, sentence] of ENGLISH) {
    const m = pattern.exec(message);
    if (m) return typeof sentence === "string" ? sentence : sentence(m);
  }
  return undefined;
}

/**
 * The user-facing sentence for an openapi-fetch `error` value.
 *
 * @param error    the `error` returned by `api.GET/POST/PUT/PATCH/DELETE`
 * @param fallback the hook's own sentence, used when the server gave no usable reason
 */
export function serverErrorMessage(error: unknown, fallback: string): string {
  const body = (error as { error?: { code?: unknown; message?: unknown } } | null | undefined)?.error;
  const message = typeof body?.message === "string" ? body.message.trim() : "";
  const code = typeof body?.code === "string" ? body.code : "";
  if (message && VIETNAMESE_LETTER.test(message)) return vietnameseNotFound(message) ?? message;
  if (message) {
    const translated = translateEnglish(message);
    if (translated) return translated;
  }
  const byCode = BY_CODE[code];
  if (byCode) return byCode;
  // The two refusals that carry no reason at all still say what kind of
  // failure it was, after the caller's sentence that names what failed: an
  // unexpected server error, or a body that is not our JSON envelope — a
  // Cloudflare/Caddy 502/504 page while the server restarts, or gin's
  // "404 page not found" while the web is deployed ahead of the server.
  // Customer pages share this path, so the cause is worded for anyone.
  if (code === "internal") return withCause(fallback, "máy chủ đang gặp lỗi, thử lại sau ít phút");
  if (typeof error === "string") {
    return withCause(fallback, "máy chủ đang cập nhật hoặc tạm gián đoạn, thử lại sau ít phút");
  }
  return fallback;
}

/**
 * "<fallback> — <cause>"; a fallback that is already a full sentence ("… thử
 * lại giúp em ạ.") or already has a dash clause ("… — thử lại") gets the cause
 * as a sentence of its own instead of a second dash.
 */
function withCause(fallback: string, cause: string): string {
  const base = fallback.trim();
  // An empty fallback is a caller asking "did the server give a reason?"
  // (staff save, gallery) — keep answering "no" so it can pick its own sentence.
  if (!base) return "";
  if (!/[.!?…]$/u.test(base) && !base.includes(" — ")) return `${base} — ${cause}`;
  return `${base.replace(/[.!?…]+$/u, "")}. ${cause.charAt(0).toUpperCase()}${cause.slice(1)}`;
}

/**
 * The server's message exactly as sent, trimmed; "" when there is none.
 *
 * Only for code that must look inside it before choosing a sentence: a
 * machine-readable marker ("[sale_line_not_assigned]", "extra_photo_line_locked")
 * or a feature's own wording for a few known refusals (ekip claims, check-in).
 * Never show it — the sentence on screen always comes from serverErrorMessage.
 * Code outside this file reads the envelope only through here and
 * serverErrorCode: src/__tests__/server-reason-no-self-unwrap.test.ts fails on
 * any other reader.
 */
export function serverRawMessage(error: unknown): string {
  const body = (error as { error?: { message?: unknown } } | null | undefined)?.error;
  return typeof body?.message === "string" ? body.message.trim() : "";
}

/**
 * The server's machine code ("dispatch_confirm_required", "version_conflict"…);
 * "" when there is none. For screens that branch on the refusal — keep the
 * code on the error they throw — never for the words they show.
 */
export function serverErrorCode(error: unknown): string {
  const body = (error as { error?: { code?: unknown } } | null | undefined)?.error;
  return typeof body?.code === "string" ? body.code : "";
}

/**
 * True for the TypeError `fetch` rejects with when the server cannot be reached
 * at all (offline, DNS, server down, blocked by CORS): "Failed to fetch" in
 * Chrome, "NetworkError when attempting to fetch resource." in Firefox, "Load
 * failed" in Safari, "fetch failed" in Node. Aborts are a DOMException and stay
 * untouched.
 */
export function isNetworkFailure(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed|network connection was lost|fetch failed/i.test(error.message)
  );
}
